import { 
  Scene, 
  Engine, 
  Camera, 
  RenderTargetTexture, 
  Mesh, 
  ShaderMaterial, 
  VertexData, 
  Color4
} from 'babylonjs';

export class SceneFadeController {
  private static readonly FADE_LAYER_MASK = 0x10000000;
  private static readonly DEFAULT_LAYER_MASK = 0x0FFFFFFF;
  private static readonly PASS_DISABLE_ALPHA_THRESHOLD = 0.999;

  private scene: Scene;
  private engine: Engine;
  private camera: Camera;
  
  private fadeAlpha: number = 1.0;
  private renderTarget: RenderTargetTexture | null = null;
  private fadeQuad: Mesh | null = null;
  private fadeMaterial: ShaderMaterial | null = null;
  private syncObserver: any = null;
  private resizeObserver: any = null;
  private meshObserver: any = null;
  private isInitialized: boolean = false;
  private currentAnimationId: number = 0;
  private lastWidth: number = 0;
  private lastHeight: number = 0;
  private fadePassEnabled: boolean = false;
  private trackedMeshLayerMasks: Map<Mesh, number> = new Map();

  constructor(scene: Scene, engine: Engine, camera: Camera) {
    this.scene = scene;
    this.engine = engine;
    this.camera = camera;
  }

  // Initialize fade effect with starting alpha value
  public initialize(initialAlpha: number = 1.0): void {
    if (this.isInitialized) {
      console.warn('SceneFadeController already initialized');
      return;
    }

    this.fadeAlpha = Math.max(0, Math.min(1, initialAlpha));

    // Use hardware scaling for proper resolution on high-DPI displays and WebXR
    const width = this.engine.getRenderWidth(true);
    const height = this.engine.getRenderHeight(true);
    this.lastWidth = width;
    this.lastHeight = height;

    this.renderTarget = new RenderTargetTexture(
      "sceneFadeRT",
      { width, height },
      this.scene,
      false,  // generateMipMaps
      true,   // doNotChangeAspectRatio
      undefined, // type
      false,  // isCube
      undefined, // samplingMode - use default (bilinear)
      true,   // generateDepthBuffer
      false,  // generateStencilBuffer
      undefined, // isMulti
      undefined, // format
      false   // delayAllocation
    );

    this.renderTarget.clearColor = new Color4(0, 0, 0, 0);
    this.renderTarget.activeCamera = this.scene.activeCamera || this.camera;
    this.renderTarget.refreshRate = 1;

    this.renderTarget.renderList = [];
    this.meshObserver = this.scene.onNewMeshAddedObservable.add((mesh) => {
      if (!this.renderTarget || this.shouldExcludeFromFade(mesh) || !this.fadePassEnabled) return;
      if (!this.renderTarget.renderList?.includes(mesh)) {
        this.renderTarget.renderList?.push(mesh);
      }
      this.trackAndSetFadeLayer(mesh as Mesh);
    });

    // Keep RT camera aligned with current scene camera and check size changes.
    // Using the active camera directly avoids matrix-copy desync issues in built WebXR bundles.
    this.syncObserver = this.scene.onBeforeRenderObservable.add(() => {
      if (this.renderTarget) {
        this.renderTarget.activeCamera = this.scene.activeCamera || this.camera;
      }
      this.checkAndUpdateSize();
    });
    
    // Also listen for engine resize events
    this.resizeObserver = this.engine.onResizeObservable.add(() => {
      this.updateSize();
    });

    // Create shader material with inline shader code
    this.fadeMaterial = new ShaderMaterial(
      "sceneFadeMaterial",
      this.scene,
      {
        vertexSource: `
          precision highp float;
          attribute vec2 position;
          varying vec2 vUV;
          
          void main(void) {
            vUV = position * 0.5 + 0.5;
            gl_Position = vec4(position, 0.0, 1.0);
          }
        `,
        fragmentSource: `
          precision highp float;
          varying vec2 vUV;
          uniform sampler2D textureSampler;
          uniform float fadeAlpha;
          
          void main(void) {
            vec4 color = texture2D(textureSampler, vUV);
            color.a *= fadeAlpha;
            if (color.a < 0.001) discard;
            gl_FragColor = color;
          }
        `,
      },
      {
        attributes: ["position"],
        uniforms: ["fadeAlpha"],
        samplers: ["textureSampler"],
      }
    );

    this.fadeMaterial.setTexture("textureSampler", this.renderTarget);
    this.fadeMaterial.setFloat("fadeAlpha", this.fadeAlpha);
    this.fadeMaterial.backFaceCulling = false;
    this.fadeMaterial.alphaMode = Engine.ALPHA_COMBINE;
    this.fadeMaterial.needAlphaBlending = () => true;
    this.fadeMaterial.disableDepthWrite = true;

    this.createFullscreenQuad();
    this.updateFadePassState();

    this.isInitialized = true;
  }

  // Fade to target alpha over duration
  public fade(alpha: number, duration: number = 0): void {
    if (!this.isInitialized || !this.fadeMaterial) {
      console.warn('SceneFadeController not initialized. Call initialize() first.');
      return;
    }

    const targetAlpha = Math.max(0, Math.min(1, alpha));
    
    // Cancel any running animation by incrementing the ID
    this.currentAnimationId++;
    const animationId = this.currentAnimationId;

    if (duration > 0) {
      const startAlpha = this.fadeAlpha;
      const startTime = Date.now();

      const animate = () => {
        // Check if this animation has been superseded
        if (animationId !== this.currentAnimationId) {
          return; // A new animation started, stop this one
        }
        
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const easeProgress = progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        
        this.fadeAlpha = startAlpha + (targetAlpha - startAlpha) * easeProgress;
        
        if (this.fadeMaterial) {
          this.fadeMaterial.setFloat("fadeAlpha", this.fadeAlpha);
        }
        this.updateFadePassState();

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          this.fadeAlpha = targetAlpha;
          if (this.fadeMaterial) {
            this.fadeMaterial.setFloat("fadeAlpha", this.fadeAlpha);
          }
          this.updateFadePassState();
        }
      };
      animate();
    } else {
      this.fadeAlpha = targetAlpha;
      this.fadeMaterial.setFloat("fadeAlpha", this.fadeAlpha);
      this.updateFadePassState();
    }
  }

  // Reset to full opacity
  public reset(duration: number = 0): void {
    this.fade(1.0, duration);
  }

  public get alpha(): number {
    return this.fadeAlpha;
  }

  public setEnabled(enabled: boolean): void {
    if (this.fadeQuad) {
      this.fadeQuad.setEnabled(enabled);
    }
    if (!enabled) {
      this.disableFadePass();
    } else {
      this.updateFadePassState();
    }
  }

  // Check if size changed and update if needed (called every frame)
  private checkAndUpdateSize(): void {
    if (!this.renderTarget) return;
    
    const width = this.engine.getRenderWidth(true);
    const height = this.engine.getRenderHeight(true);
    
    // Only resize if dimensions actually changed (avoids unnecessary work)
    if (width !== this.lastWidth || height !== this.lastHeight) {
      this.lastWidth = width;
      this.lastHeight = height;
      this.renderTarget.resize({ width, height });
    }
  }

  // Update render target size on window resize
  public updateSize(): void {
    if (!this.renderTarget) return;

    const width = this.engine.getRenderWidth(true);
    const height = this.engine.getRenderHeight(true);
    
    this.lastWidth = width;
    this.lastHeight = height;
    this.renderTarget.resize({ width, height });
  }

  public dispose(): void {
    if (this.syncObserver) {
      this.scene.onBeforeRenderObservable.remove(this.syncObserver);
      this.syncObserver = null;
    }
    
    if (this.resizeObserver) {
      this.engine.onResizeObservable.remove(this.resizeObserver);
      this.resizeObserver = null;
    }

    if (this.meshObserver) {
      this.scene.onNewMeshAddedObservable.remove(this.meshObserver);
      this.meshObserver = null;
    }

    this.disableFadePass();

    this.scene.meshes.forEach((mesh) => {
      if (!this.shouldExcludeFromFade(mesh) && mesh.layerMask === SceneFadeController.FADE_LAYER_MASK) {
        mesh.layerMask = SceneFadeController.DEFAULT_LAYER_MASK;
      }
    });

    if (this.fadeQuad) {
      this.fadeQuad.dispose();
      this.fadeQuad = null;
    }

    if (this.fadeMaterial) {
      this.fadeMaterial.dispose();
      this.fadeMaterial = null;
    }

    if (this.renderTarget) {
      const index = this.scene.customRenderTargets.indexOf(this.renderTarget);
      if (index > -1) {
        this.scene.customRenderTargets.splice(index, 1);
      }
      this.renderTarget.dispose();
      this.renderTarget = null;
    }

    this.isInitialized = false;
  }

  /** Check if mesh should be excluded from fade effect */
  private shouldExcludeFromFade(mesh: { name: string }): boolean {
    // Exclude internal fade quad and placement reticle
    return mesh.name === 'sceneFadeQuad' || mesh.name === 'placementReticle';
  }

  private updateFadePassState(): void {
    if (this.fadeAlpha < SceneFadeController.PASS_DISABLE_ALPHA_THRESHOLD) {
      this.enableFadePass();
    } else {
      this.disableFadePass();
    }
  }

  private trackAndSetFadeLayer(mesh: Mesh): void {
    if (!this.trackedMeshLayerMasks.has(mesh)) {
      this.trackedMeshLayerMasks.set(mesh, mesh.layerMask);
    }
    mesh.layerMask = SceneFadeController.FADE_LAYER_MASK;
  }

  private enableFadePass(): void {
    if (this.fadePassEnabled || !this.renderTarget) return;

    this.renderTarget.renderList = [];
    for (const mesh of this.scene.meshes) {
      if (this.shouldExcludeFromFade(mesh)) continue;
      this.trackAndSetFadeLayer(mesh as Mesh);
      this.renderTarget.renderList.push(mesh);
    }

    if (!this.scene.customRenderTargets.includes(this.renderTarget)) {
      this.scene.customRenderTargets.push(this.renderTarget);
    }
    this.fadeQuad?.setEnabled(true);
    this.fadePassEnabled = true;
  }

  private disableFadePass(): void {
    if (!this.fadePassEnabled || !this.renderTarget) return;

    for (const [mesh, originalLayerMask] of this.trackedMeshLayerMasks.entries()) {
      if (!mesh.isDisposed()) {
        mesh.layerMask = originalLayerMask;
      }
    }
    this.trackedMeshLayerMasks.clear();

    this.renderTarget.renderList = [];
    const index = this.scene.customRenderTargets.indexOf(this.renderTarget);
    if (index > -1) {
      this.scene.customRenderTargets.splice(index, 1);
    }
    this.fadeQuad?.setEnabled(false);
    this.fadePassEnabled = false;
  }

  private createFullscreenQuad(): void {
    const positions = [
      -1, -1, 0,
       1, -1, 0,
       1,  1, 0,
      -1,  1, 0,
    ];

    const indices = [
      0, 1, 2,
      0, 2, 3,
    ];

    this.fadeQuad = new Mesh("sceneFadeQuad", this.scene);
    this.fadeQuad.renderingGroupId = 3;
    this.fadeQuad.alwaysSelectAsActiveMesh = true;
    this.fadeQuad.isPickable = false;
    this.fadeQuad.layerMask = SceneFadeController.DEFAULT_LAYER_MASK;

    const vertexData = new VertexData();
    vertexData.positions = positions;
    vertexData.indices = indices;
    vertexData.applyToMesh(this.fadeQuad);

    this.fadeQuad.material = this.fadeMaterial;
    this.fadeQuad.setEnabled(false);
  }
}
